import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { initFirebase, watchAuth, signOutNow, resendVerification } from './lib/firebase';
import { api } from './lib/api';
import { Button, ErrorBanner, Spinner } from './components/ui';
import Layout, { ROUTE_MIN_ROLE } from './components/Layout';
import RequireRole from './components/RequireRole';
import ErrorBoundary from './components/ErrorBoundary';
import { companyBase } from './lib/paths';

import SignIn from './pages/SignIn';
import Companies from './pages/Companies';
import Dashboard from './pages/Dashboard';
import CapTable from './pages/CapTable';
import Stakeholders from './pages/Stakeholders';
import Securities from './pages/Securities';
import Rounds from './pages/Rounds';
import Waterfall from './pages/Waterfall';
import Documents from './pages/Documents';
import Structure from './pages/Structure';
import Valuations from './pages/Valuations';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import Members from './pages/Members';
import Review from './pages/Review';
import Agent from './pages/Agent';

const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

const CompanyContext = createContext(null);
export const useCompany = () => useContext(CompanyContext);

/** Wraps a route's element with the role check ROUTE_MIN_ROLE declares for
 * that segment -- the single source of truth Layout's nav filter also
 * reads, so the two can't drift apart. */
const gated = (segment, role, element) => (
  <RequireRole role={role} minRole={ROUTE_MIN_ROLE[segment]}>{element}</RequireRole>
);

export default function App() {
  const [boot, setBoot] = useState({ state: 'loading' });
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);

  // Boot: load runtime config + Firebase, then subscribe to auth state.
  useEffect(() => {
    let unsubscribe = () => {};
    initFirebase()
      .then(() => {
        unsubscribe = watchAuth((user) => {
          setFirebaseUser(user);
          setBoot({ state: 'ready' });
        });
      })
      .catch((err) => setBoot({ state: 'error', error: err }));
    return () => unsubscribe();
  }, []);

  // Once signed in, resolve the server-side profile. This is also what creates
  // the app_users row on first contact.
  const loadProfile = useCallback(async () => {
    setProfileError(null);
    try {
      setProfile(await api.me());
    } catch (err) {
      setProfileError(err);
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    if (firebaseUser) loadProfile();
    else setProfile(null);
  }, [firebaseUser, loadProfile]);

  const session = useMemo(
    () => ({ firebaseUser, profile, reloadProfile: loadProfile }),
    [firebaseUser, profile, loadProfile]
  );

  if (boot.state === 'loading') return <FullPage><Spinner label="Starting" /></FullPage>;

  if (boot.state === 'error') {
    return (
      <FullPage>
        <div className="boot-error">
          <h1>Cannot start</h1>
          <p>{boot.error?.message}</p>
          <p className="muted">
            The server must serve a Firebase config at <code>/config.json</code>. Check
            FIREBASE_API_KEY and FIREBASE_AUTH_DOMAIN on the deployment.
          </p>
        </div>
      </FullPage>
    );
  }

  if (!firebaseUser) return <SignIn />;

  // The API refuses unverified emails (403) on purpose — surface that here
  // rather than letting every request fail mysteriously.
  if (firebaseUser.email && firebaseUser.emailVerified === false) {
    return <VerifyEmail user={firebaseUser} />;
  }

  if (profileError) {
    return (
      <FullPage>
        <div className="boot-error">
          <h1>Signed in, but the API refused</h1>
          <ErrorBanner error={profileError} onRetry={loadProfile} />
          <Button onClick={() => signOutNow()}>Sign out</Button>
        </div>
      </FullPage>
    );
  }

  if (!profile) return <FullPage><Spinner label="Loading your account" /></FullPage>;

  return (
    <SessionContext.Provider value={session}>
      <Routes>
        <Route path="/" element={<Companies />} />
        <Route path="/companies/:companyId/*" element={<CompanyRoutes />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionContext.Provider>
  );
}

/** Resolves the company once, then renders the app shell around its pages. */
function CompanyRoutes() {
  const { companyId } = useParams();
  const location = useLocation();
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      setState({ loading: false, company: await api.company(companyId) });
    } catch (error) {
      setState({ loading: false, error });
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <FullPage><Spinner label="Loading company" /></FullPage>;

  if (state.error) {
    return (
      <FullPage>
        <div className="boot-error">
          <h1>Company unavailable</h1>
          <ErrorBanner error={state.error} onRetry={load} />
          <a href="/">Back to your companies</a>
        </div>
      </FullPage>
    );
  }

  const role = state.company.yourRole;
  const value = { company: state.company, companyId, reload: load, role };

  return (
    <CompanyContext.Provider value={value}>
      <Layout>
        {/* Scoped to the routed view so a throwing tab keeps the nav usable,
            and keyed on the path so navigating away clears the error. */}
        <ErrorBoundary resetKey={location.pathname}>
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="cap-table" element={gated('cap-table', role, <CapTable />)} />
            <Route path="stakeholders" element={<Stakeholders />} />
            <Route path="securities" element={<Securities />} />
            <Route path="rounds" element={<Rounds />} />
            <Route path="waterfall" element={gated('waterfall', role, <Waterfall />)} />
            <Route path="documents" element={<Documents />} />
            <Route path="structure" element={<Structure />} />
            <Route path="valuations" element={<Valuations />} />
            <Route path="review" element={gated('review', role, <Review />)} />
            <Route path="assistant" element={gated('assistant', role, <Agent />)} />
            <Route path="members" element={gated('members', role, <Members />)} />
            <Route path="audit" element={gated('audit', role, <AuditLog />)} />
            <Route path="settings" element={gated('settings', role, <Settings />)} />
            {/* Absolute, not "." — inside a splat route a relative target
                resolves against the matched pathname including the splat, so
                this redirect could send an unmatched URL back to itself and
                render nothing at all. That is what turned an accumulated path
                into a permanently blank view instead of a bounce to Overview. */}
            <Route path="*" element={<Navigate to={companyBase(companyId)} replace />} />
          </Routes>
        </ErrorBoundary>
      </Layout>
    </CompanyContext.Provider>
  );
}

function VerifyEmail({ user }) {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  return (
    <FullPage>
      <div className="boot-error">
        <h1>Verify your email</h1>
        <p>
          A cap table controls who owns what, so the API only accepts verified email
          addresses — an unverified address can be changed at will, which would let
          someone claim an invitation addressed to a mailbox they do not control.
        </p>
        <p className="muted">Signed in as {user.email}</p>
        {error ? <ErrorBanner error={error} /> : null}
        <div className="row-actions">
          <Button
            variant="primary"
            onClick={async () => {
              try {
                await resendVerification();
                setSent(true);
              } catch (err) {
                setError(err);
              }
            }}
          >
            {sent ? 'Verification sent' : 'Send verification email'}
          </Button>
          <Button onClick={() => window.location.reload()}>I have verified — reload</Button>
          <Button onClick={() => signOutNow()}>Sign out</Button>
        </div>
      </div>
    </FullPage>
  );
}

function FullPage({ children }) {
  return <div className="full-page">{children}</div>;
}
